import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function ConsultationPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    city: '',
    mobile: '',
    whatsapp: '',
    gender: '',
    location: '',
    medicalHistory: '',
    currentMedications: '',
    notes: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuccess(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const steps = [
    { number: 1, title: 'المعلومات الشخصية', icon: 'ri-user-line' },
    { number: 2, title: 'المعلومات الطبية', icon: 'ri-heart-pulse-line' },
    { number: 3, title: 'ملاحظات إضافية', icon: 'ri-file-text-line' }
  ];

  if (showSuccess) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-24 pb-20">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl shadow-2xl p-12 text-center animate-fade-in">
                <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-checkbox-circle-line text-primary text-6xl"></i>
                </div>
                <h1 className="text-4xl font-bold text-primary mb-4">تم استلام طلبك بنجاح</h1>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  طلبك قيد المراجعة من قبل الفريق الطبي وسيتم التواصل معك قريباً عبر الواتساب أو الهاتف
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => {
                      setShowSuccess(false);
                      setCurrentStep(1);
                      setFormData({
                        fullName: '',
                        city: '',
                        mobile: '',
                        whatsapp: '',
                        gender: '',
                        location: '',
                        medicalHistory: '',
                        currentMedications: '',
                        notes: ''
                      });
                    }}
                    className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-lg font-medium hover:bg-primary-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                    <span>طلب جديد</span>
                  </button>
                  <a
                    href="/"
                    className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-8 py-4 rounded-lg font-medium hover:bg-gray-200 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-home-line"></i>
                    <span>العودة للرئيسية</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <Header />
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12 animate-fade-in">
              <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">طلب استشارة طبية</h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                املأ البيانات التالية وسيتواصل معك <strong>الفريق الطبي قريباً</strong>
              </p>
            </div>

            {/* Progress Steps */}
            <div className="mb-12">
              <div className="flex justify-between items-center max-w-2xl mx-auto">
                {steps.map((step, index) => (
                  <div key={step.number} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        currentStep >= step.number
                          ? 'bg-primary text-white shadow-lg'
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        <i className={`${step.icon} text-xl`}></i>
                      </div>
                      <span className={`text-sm mt-2 font-medium ${
                        currentStep >= step.number ? 'text-primary' : 'text-gray-500'
                      }`}>
                        {step.title}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`h-1 flex-1 mx-2 transition-all duration-300 ${
                        currentStep > step.number ? 'bg-primary' : 'bg-gray-200'
                      }`}></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 animate-slide-up">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      <i className="ri-user-line ml-2"></i>
                      الاسم الكامل *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors text-lg"
                      placeholder="أدخل اسمك الكامل"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        <i className="ri-map-pin-line ml-2"></i>
                        المدينة *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors text-lg"
                        placeholder="المدينة"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        <i className="ri-user-3-line ml-2"></i>
                        الجنس *
                      </label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors text-lg pr-8 cursor-pointer"
                      >
                        <option value="">اختر الجنس</option>
                        <option value="male">ذكر</option>
                        <option value="female">أنثى</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        <i className="ri-phone-line ml-2"></i>
                        رقم الجوال *
                      </label>
                      <input
                        type="tel"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors text-lg"
                        placeholder="05xxxxxxxx"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        <i className="ri-whatsapp-line ml-2"></i>
                        رقم الواتساب *
                      </label>
                      <input
                        type="tel"
                        name="whatsapp"
                        value={formData.whatsapp}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors text-lg"
                        placeholder="05xxxxxxxx"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      <i className="ri-map-2-line ml-2"></i>
                      الموقع (اختياري)
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors text-lg"
                      placeholder="يمكنك كتابة العنوان أو استخدام GPS"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Medical Information */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      <i className="ri-file-list-line ml-2"></i>
                      التاريخ المرضي *
                    </label>
                    <textarea
                      name="medicalHistory"
                      value={formData.medicalHistory}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors text-lg resize-none"
                      placeholder="اذكر أي أمراض مزمنة أو عمليات جراحية سابقة..."
                    ></textarea>
                    <p className="text-sm text-gray-500 mt-2">الحد الأقصى 500 حرف</p>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      <i className="ri-medicine-bottle-line ml-2"></i>
                      الأدوية الحالية *
                    </label>
                    <textarea
                      name="currentMedications"
                      value={formData.currentMedications}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors text-lg resize-none"
                      placeholder="اذكر جميع الأدوية التي تتناولها حالياً..."
                    ></textarea>
                    <p className="text-sm text-gray-500 mt-2">الحد الأقصى 500 حرف</p>
                  </div>
                </div>
              )}

              {/* Step 3: Additional Notes */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      <i className="ri-chat-3-line ml-2"></i>
                      ملاحظات إضافية (اختياري)
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={8}
                      maxLength={500}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors text-lg resize-none"
                      placeholder="أي معلومات إضافية تود مشاركتها مع الفريق الطبي..."
                    ></textarea>
                    <p className="text-sm text-gray-500 mt-2">الحد الأقصى 500 حرف</p>
                  </div>

                  <div className="bg-primary/5 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-primary mb-3">ملخص البيانات</h3>
                    <div className="space-y-2 text-gray-700">
                      <p><strong>الاسم:</strong> {formData.fullName}</p>
                      <p><strong>المدينة:</strong> {formData.city}</p>
                      <p><strong>الجوال:</strong> {formData.mobile}</p>
                      <p><strong>الواتساب:</strong> {formData.whatsapp}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-8 border-t-2 border-gray-100">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(prev => prev - 1)}
                    className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-right-line"></i>
                    <span>رجوع</span>
                  </button>
                )}

                {currentStep < 3 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(prev => prev + 1)}
                    className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer mr-auto"
                  >
                    <span>التالي</span>
                    <i className="ri-arrow-left-line"></i>
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-lg font-medium hover:bg-primary-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer mr-auto"
                  >
                    <i className="ri-send-plane-line"></i>
                    <span>إرسال الطلب</span>
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}